import { useState } from "react"
import { motion } from "framer-motion"
import {ShoppingBag} from 'lucide-react'
import { products } from "@/data/products"
import { useLang } from "@/context/LanguageContext"
import { useCart } from "@/context/CartContext"

function ProductCard({ product, index }: { product: typeof products[0]; index: number }) {
  const { lang, t } = useLang()
  const { addItem } = useCart()
  const isOnRequest = product.price === 0
  const isSoldOut = Boolean(product.soldOut)
  const hasMultipleSizes = product.sizes.length > 1
  const [selectedSize, setSelectedSize] = useState<string>(hasMultipleSizes ? "" : product.sizes[0])
  const [sizeError, setSizeError] = useState(false)

  const handleAddToCart = () => {
    if (!selectedSize) {
      setSizeError(true)
      return
    }
    setSizeError(false)
    addItem(product, selectedSize, lang)
  }

  const sizeLabelFr = product.sizes.length > 1 ? product.sizes.join(" ou ") : product.sizes[0]
  const sizeLabelEn = product.sizes.length > 1 ? product.sizes.join(" or ") : product.sizes[0]

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group relative bg-white border transition-all duration-700"
      style={{ borderColor: "rgba(130, 57, 31, 0.08)" }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = "rgba(225, 89, 63, 0.2)")}
      onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(130, 57, 31, 0.08)")}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden" style={{ backgroundColor: "#F0E8DE" }}>
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "rgba(130, 57, 31, 0.25)" }}>
              {t("Photo à venir", "Photo coming soon")}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {product.launchOffer && !isSoldOut && (
            <span className="px-2 py-1 text-[9px] tracking-[0.15em] uppercase text-white" style={{ backgroundColor: "#E1593F" }}>
              {t("Offre de lancement", "Launch offer")}
            </span>
          )}
          {isSoldOut && (
            <span className="px-2 py-1 text-[9px] tracking-[0.15em] uppercase text-white" style={{ backgroundColor: "rgba(13, 12, 11, 0.55)" }}>
              {t("Épuisé", "Sold out")}
            </span>
          )}
        </div>

        {/* Quick add button */}
        {!isOnRequest && !isSoldOut && (
          <motion.button
            onClick={handleAddToCart}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="absolute bottom-4 left-4 right-4 py-3 text-white text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500"
            style={{ backgroundColor: "rgba(225, 89, 63, 0.95)" }}
          >
            <ShoppingBag size={14} strokeWidth={1.5} />
            {t("Ajouter au panier", "Add to cart")}
          </motion.button>
        )}

        {isOnRequest && !isSoldOut && (
          <div
            className="absolute bottom-4 left-4 right-4 py-3 backdrop-blur-sm text-white text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500"
            style={{ backgroundColor: "rgba(130, 57, 31, 0.6)" }}
          >
            {t("Sur demande", "On request")}
          </div>
        )}

        {isSoldOut && (
          <div
            className="absolute bottom-4 left-4 right-4 py-3 text-white text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 cursor-not-allowed"
            style={{ backgroundColor: "rgba(13, 12, 11, 0.45)" }}
          >
            {t("Épuisé", "Sold out")}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-5">
        <p className="text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: "rgba(225, 89, 63, 0.6)" }}>
          {product.category}
        </p>
        <h3 className="text-xl mb-1" style={{ color: "#82391F", letterSpacing: "4px" }}>
          {product.name}
        </h3>
        <p className="text-[10px] tracking-wider mb-3" style={{ color: "rgba(13, 12, 11, 0.3)" }}>
          {t("Réf.", "Ref.")} {product.ref}
        </p>
        <p className="leading-relaxed mb-4 line-clamp-2" style={{ color: "rgba(13, 12, 11, 0.4)", fontSize: "14px" }}>
          {isSoldOut
            ? (lang === "fr" ? product.description : product.descriptionEn)
            : (lang === "fr" ? `Regular \u2014 Nylon 6/6, ${sizeLabelFr}` : `Regular \u2014 Nylon 6/6, ${sizeLabelEn}`)}
        </p>

        {/* Size selector */}
        {!isSoldOut ? (
          <div className="mb-4">
            <div className="flex gap-2">
              {product.sizes.map(size => (
                <button
                  key={size}
                  type="button"
                  onClick={() => { setSelectedSize(size); setSizeError(false) }}
                  className="px-3 py-1.5 text-xs tracking-wider uppercase border transition-colors duration-300"
                  style={{
                    borderColor: selectedSize === size ? "#E1593F" : "rgba(130, 57, 31, 0.15)",
                    color: selectedSize === size ? "#E1593F" : "rgba(13, 12, 11, 0.5)",
                    backgroundColor: selectedSize === size ? "rgba(225, 89, 63, 0.06)" : "transparent",
                  }}
                >
                  {size}
                </button>
              ))}
            </div>
            {sizeError && (
              <p className="mt-2 text-xs" style={{ color: "#E1593F" }}>
                {t("Veuillez sélectionner une taille", "Please select a size")}
              </p>
            )}
          </div>
        ) : null}

        {/* Price */}
        <div className="flex items-end justify-between">
          {product.originalPrice ? (
            <span style={{ fontSize: "17px" }}>
              <span style={{ color: "rgba(13, 12, 11, 0.3)", textDecoration: "line-through", marginRight: "8px" }}>
                {lang === "fr" ? `${product.originalPrice.toFixed(2).replace(".", ",")} \u20AC` : `\u20AC${product.originalPrice.toFixed(2)}`}
              </span>
              <span style={{ color: "#E1593F", fontWeight: 600 }}>
                {lang === "fr" ? `${product.price.toFixed(2).replace(".", ",")} \u20AC` : `\u20AC${product.price.toFixed(2)}`}
              </span>
              <span style={{ color: "rgba(13, 12, 11, 0.4)", marginLeft: "6px" }}>
                {lang === "fr" ? "la pochette de 5" : "per pouch of 5"}
              </span>
            </span>
          ) : (
            <span style={{ color: "#0D0C0B", fontSize: "17px" }}>
              {lang === "fr" ? product.priceLabel : product.priceLabelEn}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  )
}

const gammes = [
  {
    name: "Regular",
    image: "https://static.lumi.new/1a/1af54d2196f81a79dc1435372c64803c.webp",
    descFr: "28 couleurs pures. L'essentiel.",
    descEn: "28 pure colors. The essential.",
  },
  {
    name: "Rimmed",
    image: "https://static.lumi.new/be/be31ca5b50d595f3c42cefc948b56470.webp",
    descFr: "Finition cerclée métal, or ou argent. L'élégance raffinée.",
    descEn: "Metal-rimmed finish, gold or silver. Refined elegance.",
    priceFr: "Sur demande",
    priceEn: "On request",
    subjectFr: "Demande d'information — Gamme Rimmed",
    subjectEn: "Information request — Rimmed range",
    bodyFr: "Bonjour, je souhaite en savoir plus sur la gamme Rimmed. Merci.",
    bodyEn: "Hello, I would like to know more about the Rimmed range. Thank you.",
  },
  {
    name: "Covered",
    image: "https://static.lumi.new/c1/c1034f9537986cedabd947a6c447731d.webp",
    descFr: "Habillé de cuir ou de suède. Texture et luxe.",
    descEn: "Dressed in leather or suede. Texture and luxury.",
    priceFr: "Sur demande",
    priceEn: "On request",
    subjectFr: "Demande d'information — Gamme Covered",
    subjectEn: "Information request — Covered range",
    bodyFr: "Bonjour, je souhaite en savoir plus sur la gamme Covered. Merci.",
    bodyEn: "Hello, I would like to know more about the Covered range. Thank you.",
  },
  {
    name: "Sublimated",
    image: "https://static.lumi.new/df/df678f1ec6876a286a2035ac6698292a.webp",
    descFr: "Motifs artistiques inspirés de Vasarely, Kandinsky, marbre. Le bouton devient \u0153uvre d'art.",
    descEn: "Artistic patterns inspired by Vasarely, Kandinsky, marble. The button becomes art.",
    priceFr: "Sur demande",
    priceEn: "On request",
    subjectFr: "Demande d'information — Gamme Sublimated",
    subjectEn: "Information request — Sublimated range",
    bodyFr: "Bonjour, je souhaite en savoir plus sur la gamme Sublimated. Merci.",
    bodyEn: "Hello, I would like to know more about the Sublimated range. Thank you.",
  },
]

export default function CatalogueSection() {
  const { t, lang } = useLang()

  return (
    <section id="catalogue" className="relative py-32" style={{ backgroundColor: "#F8F2EB" }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: "rgba(225, 89, 63, 0.6)" }}>
            {t("Nos créations", "Our creations")}
          </p>
          <h2 className="text-4xl md:text-5xl mb-6" style={{ color: "#82391F", letterSpacing: "4px" }}>
            {t("La Collection", "The Collection")}
          </h2>
          <div className="w-16 h-px mx-auto mb-6" style={{ backgroundColor: "rgba(225, 89, 63, 0.35)" }} />
          <p className="max-w-lg mx-auto leading-relaxed" style={{ color: "rgba(13, 12, 11, 0.45)", fontSize: "17px" }}>
            {t(
              "Quatre gammes, une passion. De la couleur pure à l'\u0153uvre d'art.",
              "Four ranges, one passion. From pure color to fine art."
            )}
          </p>
        </motion.div>

        {/* Gammes section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <p className="text-xs tracking-[0.4em] uppercase mb-8 text-center" style={{ color: "rgba(225, 89, 63, 0.6)" }}>
            {t("Les gammes", "The ranges")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {gammes.map((gamme, i) => (
              <motion.div
                key={gamme.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group p-6 bg-white border text-center transition-all duration-700"
                style={{ borderColor: "rgba(130, 57, 31, 0.08)" }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = "rgba(225, 89, 63, 0.2)")}
                onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(130, 57, 31, 0.08)")}
              >
                <div className="mb-4 flex justify-center">
                  <img
                    src={gamme.image}
                    alt={gamme.name}
                    className="w-48 h-48 object-contain"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-xl mb-3" style={{ color: "#82391F", letterSpacing: "4px" }}>
                  {gamme.name}
                </h3>
                <p className="leading-relaxed mb-4" style={{ color: "rgba(13, 12, 11, 0.4)", fontSize: "15px" }}>
                  {lang === "fr" ? gamme.descFr : gamme.descEn}
                </p>
                {gamme.priceFr && (
                  <div style={{ fontSize: "17px" }}>
                    <span style={{ color: "#E1593F" }}>
                      {lang === "fr" ? gamme.priceFr : gamme.priceEn}
                    </span>
                  </div>
                )}
                {gamme.subjectFr && (
                  <a
                    href={`mailto:info@clips-on.ch?subject=${encodeURIComponent(lang === "fr" ? gamme.subjectFr : gamme.subjectEn!)}&body=${encodeURIComponent(lang === "fr" ? gamme.bodyFr! : gamme.bodyEn!)}`}
                    className="inline-block mt-4 px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-all duration-300"
                    style={{
                      color: "#E1593F",
                      borderColor: "#E1593F",
                      backgroundColor: "transparent",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = "#E1593F"
                      e.currentTarget.style.color = "#FFFFFF"
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = "transparent"
                      e.currentTarget.style.color = "#E1593F"
                    }}
                  >
                    {t("Nous contacter", "Contact us")}
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
